<script setup>
import AppLayout from '@/Layouts/AppLayout.vue'
import PageShell from '@/Components/UI/PageShell.vue'
import ConfirmDialog from '@/Components/UI/ConfirmDialog.vue'
import { Link, router } from '@inertiajs/vue3'
import { ref, watch } from 'vue'

defineOptions({ layout: AppLayout })

const props = defineProps({
  account: { type: Object, required: true },
  temp_password: { type: String, default: null }
})

const showDeleteConfirm = ref(false)

// The server passes a fresh one-time password as a prop after create or
// reset (via flash, so it survives exactly one redirect). Open a modal
// with a copy button; the operator dismisses it manually. Watch the
// prop because Inertia updates it via XHR — Vue setup() only runs
// once per component mount, so a plain ref(!!props.temp_password) would
// stay false when the prop transitions null → string mid-session.
const tempPasswordModalOpen = ref(false)
const copied = ref(false)

watch(() => props.temp_password, (val) => {
  if (val) tempPasswordModalOpen.value = true
}, { immediate: true })

async function copyPassword() {
  try {
    await navigator.clipboard.writeText(props.temp_password)
    copied.value = true
    setTimeout(() => { copied.value = false }, 1800)
  } catch (_err) {
    // Fallback: select the input so the operator can Ctrl+C
    document.getElementById('temp-password-field')?.select()
  }
}

function deleteAccount() {
  router.delete(`/admin/portal_accounts/${props.account.id}`)
}
</script>

<template>
  <PageShell
    :title="account.email"
    back-href="/admin/portal_accounts"
    back-label="Portal accounts"
  >
    <div class="show-layout">
      <!-- Detail card -->
      <div class="detail-card">
        <h2 class="card-heading">Account details</h2>
        <dl class="detail-grid">
          <dt>Owner</dt>
          <dd>{{ account.owner_label }}</dd>

          <dt>Email</dt>
          <dd>{{ account.email }}</dd>

          <dt>Status</dt>
          <dd>
            <span :class="`pill pill--${account.status}`">{{ account.status }}</span>
          </dd>

          <dt>Last sign-in</dt>
          <dd>{{ account.last_sign_in_at || '—' }}</dd>

          <dt>Sign-in count</dt>
          <dd>{{ account.sign_in_count }}</dd>

          <dt>Created by</dt>
          <dd>{{ account.created_by || '—' }}</dd>

          <dt>Created at</dt>
          <dd>{{ account.created_at }}</dd>

          <template v-if="account.disabled_at">
            <dt>Disabled at</dt>
            <dd>{{ account.disabled_at }}</dd>
          </template>

          <template v-if="account.locked_at">
            <dt>Locked at</dt>
            <dd>{{ account.locked_at }}</dd>
          </template>
        </dl>
      </div>

      <!-- Actions card -->
      <div class="actions-card">
        <h2 class="card-heading">Quick actions</h2>
        <div class="action-list">
          <Link
            :href="`/admin/portal_accounts/${account.id}/reset_password`"
            method="patch"
            as="button"
            class="action-btn"
          >
            Reset password
          </Link>

          <Link
            :href="`/admin/portal_accounts/${account.id}/toggle_disabled`"
            method="patch"
            as="button"
            class="action-btn"
          >
            {{ account.status === 'disabled' ? 'Enable account' : 'Disable account' }}
          </Link>

          <Link
            v-if="account.locked_at"
            :href="`/admin/portal_accounts/${account.id}/unlock`"
            method="patch"
            as="button"
            class="action-btn"
          >
            Unlock account
          </Link>

          <Link
            :href="`/admin/portal_accounts/${account.id}/edit`"
            class="action-btn"
          >
            Edit email
          </Link>

          <button
            class="action-btn action-btn--danger"
            @click="showDeleteConfirm = true"
          >
            Delete account
          </button>
        </div>
      </div>
    </div>

    <ConfirmDialog
      :open="showDeleteConfirm"
      :title="`Delete portal account for ${account.email}?`"
      consequence="This permanently removes the supplier's portal access. They will no longer be able to sign in."
      :danger="true"
      @confirm="deleteAccount"
      @cancel="showDeleteConfirm = false"
    />

    <!-- Temporary password modal — shown once after create/reset.
         Stays open until the operator dismisses; never auto-closes. -->
    <div
      v-if="tempPasswordModalOpen && temp_password"
      class="temp-pw-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="temp-pw-title"
    >
      <div class="temp-pw-modal">
        <h2 id="temp-pw-title" class="temp-pw-title">New temporary password</h2>
        <p class="temp-pw-sub">
          Share this with the supplier securely. It will not be shown again
          after you close this window.
        </p>
        <div class="temp-pw-field-row">
          <input
            id="temp-password-field"
            class="temp-pw-field"
            type="text"
            readonly
            :value="temp_password"
            @focus="$event.target.select()"
          />
          <button
            type="button"
            class="temp-pw-copy"
            :class="{ 'is-copied': copied }"
            @click="copyPassword"
          >
            {{ copied ? '✓ Copied' : 'Copy' }}
          </button>
        </div>
        <p class="temp-pw-meta">
          Account: <strong>{{ account.email }}</strong>
        </p>
        <div class="temp-pw-actions">
          <button
            type="button"
            class="temp-pw-dismiss"
            @click="tempPasswordModalOpen = false"
          >
            I've saved it
          </button>
        </div>
      </div>
    </div>
  </PageShell>
</template>

<style scoped>
.show-layout {
  display: grid;
  gap: 1.5rem;
  grid-template-columns: 1fr;
}
@media (min-width: 768px) {
  .show-layout {
    grid-template-columns: 1fr 280px;
    align-items: start;
  }
}
.detail-card,
.actions-card {
  padding: 1.25rem 1.5rem;
  border-radius: var(--radius-table, 12px);
  border: 1px solid var(--line);
  background: var(--surface);
}
.card-heading {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--inkSoft);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 1rem;
}
.detail-grid {
  display: grid;
  grid-template-columns: 10rem 1fr;
  gap: 0.5rem 1rem;
  font-size: 0.875rem;
}
.detail-grid dt {
  color: var(--muted);
}
.detail-grid dd {
  color: var(--ink);
  margin: 0;
}
.pill {
  display: inline-block;
  padding: 0.125rem 0.5rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 500;
}
.pill--active   { background: color-mix(in oklch, var(--ok) 16%, var(--surface)); color: var(--ok); }
.pill--disabled { background: color-mix(in oklch, var(--muted) 14%, var(--surface)); color: var(--muted); }
.pill--locked   { background: color-mix(in oklch, var(--warn) 14%, var(--surface)); color: var(--warn); }
.action-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.action-btn {
  display: block;
  width: 100%;
  padding: 0.5rem 1rem;
  border-radius: var(--radius-button, 8px);
  border: 1px solid var(--line);
  background: var(--surfaceAlt);
  color: var(--ink);
  font-size: 0.875rem;
  text-align: left;
  text-decoration: none;
  cursor: pointer;
}
.action-btn:hover {
  background: var(--surface);
  border-color: var(--accent);
  color: var(--accent);
}
.action-btn--danger {
  color: var(--danger);
  border-color: color-mix(in oklch, var(--danger) 30%, var(--line));
}
.action-btn--danger:hover {
  background: color-mix(in oklch, var(--danger) 8%, var(--surface));
  border-color: var(--danger);
  color: var(--danger);
}

/* Temp password modal */
.temp-pw-overlay {
  position: fixed;
  inset: 0;
  z-index: 80;
  background: rgba(0,0,0,0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}
.temp-pw-modal {
  width: 100%;
  max-width: 30rem;
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--radius-lg, 12px);
  padding: 1.5rem;
  box-shadow: 0 24px 48px -16px rgba(0,0,0,0.4);
}
.temp-pw-title {
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--ink);
  margin: 0 0 0.5rem;
}
.temp-pw-sub {
  font-size: 0.875rem;
  color: var(--inkSoft);
  margin: 0 0 1rem;
}
.temp-pw-field-row {
  display: flex;
  gap: 0.5rem;
  align-items: stretch;
  margin-bottom: 0.75rem;
}
.temp-pw-field {
  flex: 1;
  padding: 0.65rem 0.85rem;
  background: var(--surfaceAlt);
  border: 1px solid var(--line);
  border-radius: var(--radius-input, 8px);
  color: var(--ink);
  font-family: var(--font-mono, ui-monospace, SFMono-Regular, monospace);
  font-size: 1rem;
  letter-spacing: 0.05em;
}
.temp-pw-field:focus {
  outline: 2px solid var(--accent);
  outline-offset: 1px;
}
.temp-pw-copy {
  padding: 0 1rem;
  background: var(--accent);
  color: var(--accentInk);
  border: 1px solid var(--accent);
  border-radius: var(--radius-button, 8px);
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
}
.temp-pw-copy.is-copied {
  background: var(--ok);
  border-color: var(--ok);
}
.temp-pw-meta {
  font-size: 0.8125rem;
  color: var(--muted);
  margin: 0 0 1.25rem;
}
.temp-pw-actions {
  display: flex;
  justify-content: flex-end;
}
.temp-pw-dismiss {
  padding: 0.55rem 1rem;
  background: var(--surfaceAlt);
  color: var(--ink);
  border: 1px solid var(--line);
  border-radius: var(--radius-button, 8px);
  font-weight: 500;
  cursor: pointer;
}
.temp-pw-dismiss:hover {
  background: var(--surface);
  border-color: var(--accent);
  color: var(--accent);
}
</style>
